import {
  useDeferredValue,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowLeftIcon,
  CircleNotchIcon,
  InfoIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { useCitySuggest } from "common/hooks/search";
import type { UnifiedSuggestItem } from "common/apis/search";
import type { SelectedLocation } from "common/hooks";
import { useI18N } from "common/hooks";
import { defaultRegion } from "common/config";

import OnboardingCityBrowse from "../components/OnboardingCityBrowse";
import OnboardingCityRow from "../components/OnboardingCityRow";
import OnboardingModal from "../components/OnboardingModal";
import OnboardingStepHeader from "../components/OnboardingStepHeader";
import {
  cityKey,
  useOnboardingCities,
  type OnboardingCityEntry,
} from "../state/useOnboardingCities";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useManualLocation } from "@/state/manualLocation";
import { formatPlaces } from "../utils/formatPlaces";
import type { OnboardingCity } from "../types";

/** Rows in the search skeleton — enough to read as a list, not a whole screen. */
const SKELETON_ROWS = 3;

/**
 * Result-list placeholder, shown while a search has nothing current to put on
 * screen.
 *
 * A bare spinner does not work here: the covered tier answers in ~150ms, so a
 * circle appears and vanishes inside a few frames and reads as no loading state
 * at all. Something list-shaped registers in that window, and it also says
 * *what* is arriving.
 */
const SuggestionSkeleton = () => (
  <div className="flex animate-pulse flex-col gap-4">
    <div className="h-[18px] w-[140px] rounded bg-container-neutral-minimal" />
    <div className="overflow-hidden rounded-lg border border-border-token-brand-minimal">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <div
          key={index}
          className="flex min-h-[48px] items-center gap-2 border-b border-border-token-brand-minimal px-4 py-3 last:border-b-0"
        >
          <div className="h-6 w-6 shrink-0 rounded-full bg-container-neutral-minimal" />
          <div className="flex flex-1 flex-col gap-1.5 px-1">
            <div className="h-3.5 w-3/5 rounded bg-container-neutral-minimal" />
            <div className="h-3 w-2/5 rounded bg-container-neutral-minimal" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * The last counted step (4 of 4 signed in, 5 of 5 signed out) — "And where
 * should we start?".
 *
 * Picking a city **is** the commitment, so there is no Continue button and the
 * footer is omitted entirely. Signed in, that ends the flow; signed out, it
 * moves on to "Let’s jump in!" - @see `JumpInStep`.
 *
 * Two states:
 *
 * - **Empty search** — every city we hold accessibility data for, as a grid of
 *   photo cards grouped by country. The list comes from the locations facets;
 *   photos come from Wikipedia, batched (@see `useCityPhotos`).
 * - **Typing** — `useCitySuggest`'s two tiers: cities we cover, grouped by
 *   country in the order the backend returned them, then "other cities that
 *   need your help". Each tier loads and spins independently.
 *
 * The heading and the search field sit in the modal's sticky slot rather than
 * the scrolling body, so results move under the field instead of taking it with
 * them. That is a deliberate divergence from Figma, which scrolls the whole
 * search block — and the same one native made. Anything added around the field
 * has to go in the same slot or it will scroll away.
 *
 * **The picker's "Current location" and "Set location on map" rows are
 * deliberately absent.** Both are ways of *changing* where the app is pointed,
 * which belongs to someone who went looking for it from the home header. Asking
 * a first-time user for the browser's location permission, or handing them a
 * map, in the middle of a four-step introduction is a bigger ask than the
 * question on screen — and the whole premise of this build is that we have not
 * asked yet.
 *
 * Also the business branch's "Change city" picker — @see `BusinessCityStep`,
 * which passes `onPick` and its own `header`. With `onPick` a pick is handed
 * back resolved instead of being committed: nothing is written to
 * `manualLocationAtom` and the flow is not completed, because there the pick
 * only revises a proposal the visitor still has to confirm.
 */
interface CityStepProps {
  /** Replaces the step heading. Must carry `ONBOARDING_STEP_HEADING_ID`. */
  header?: ReactNode;
  /** Receives the resolved city instead of it completing the flow. */
  onPick?: (city: OnboardingCity) => void;
  /** The dialog's name when `header` replaces the step heading. */
  ariaLabel?: string;
  /**
   * A back arrow in the search field's leading slot, in place of the
   * magnifier, for a picker opened over something to go back to (design QA:
   * no ✕ in the header). Escape in the field does the same.
   */
  onBack?: () => void;
  /** The back arrow's accessible name, which says where it goes. */
  backLabel?: string;
}

const CityStep = ({
  header,
  onPick,
  ariaLabel,
  onBack,
  backLabel,
}: CityStepProps = {}) => {
  const i18n = useI18N();
  const { stepNumber, totalSteps, completeWithCity, dismiss } =
    useOnboardingFlow();

  const { setManualLocation } = useManualLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectingKey, setSelectingKey] = useState<string | null>(null);

  /*
   * The real "one pick at a time" latch. `selectingKey` drives the spinner, but
   * it is state: clicks that land before React re-renders all read the same
   * stale `null` and every one of them starts a resolve. A ref is written
   * synchronously, so the second click sees it in the same tick.
   */
  const choosingRef = useRef(false);

  /**
   * The body renders off a deferred copy of the term, the field off the live
   * one. Clearing the field flips this from a handful of rows to the whole
   * browse grid — every covered city, each an image card, in one commit.
   * Deferring lets the text clear immediately and builds the grid in a second,
   * interruptible pass.
   */
  const deferredTerm = useDeferredValue(searchTerm);
  const trimmedTerm = deferredTerm.trim();
  const isSearching = trimmedTerm.length > 0;

  const {
    resolveCity,
  } = useOnboardingCities();

  const {
    groups: coveredGroups,
    needsHelp,
    isLoadingCovered,
    isLoadingNeedsHelp,
    searchedTerm,
    selectCity,
  } = useCitySuggest(deferredTerm, {
    // Only biases ambiguous names; we have not asked where the user is.
    latitude: defaultRegion.latitude,
    longitude: defaultRegion.longitude,
  });


  const title = i18n.get("onboardingCityTitle");

  /**
   * Resolves the click to coordinates and completes the flow.
   *
   * Resolution is best-effort: a failed geocode still completes with the city
   * as picked, because stranding someone over a missing latitude would be worse
   * than a missing latitude.
   */
  const choose = async (
    city: OnboardingCity,
    resolve: () => Promise<SelectedLocation | null>,
  ) => {
    // One pick at a time. Only the clicked card used to be disabled, so a
    // second click elsewhere started a second resolve; both would finish and
    // both call `completeWithCity`, leaving whichever geocode came back last as
    // the answer - and the second one landing after the modal had unmounted.
    if (choosingRef.current) return;
    choosingRef.current = true;
    setSelectingKey(cityKey(city));

    let resolved: SelectedLocation | null = null;
    try {
      resolved = await resolve();
    } catch {
      resolved = null;
    }

    choosingRef.current = false;
    setSelectingKey(null);

    if (onPick) {
      onPick(
        resolved
          ? {
              ...city,
              latitude: resolved.latitude,
              longitude: resolved.longitude,
              placeId: resolved.placeId,
              label: resolved.simpleText || city.name,
            }
          : city,
      );
      return;
    }

    if (!resolved) {
      // Resolution is best-effort, so the flow still completes - but with no
      // coordinates there is nothing to point the page at, and it stays
      // wherever it was.
      completeWithCity(city);
      return;
    }

    // The short label is what the home header shows.
    const label = resolved.simpleText || city.name;

    /*
     * Applied here rather than derived from the answer later, which is the
     * whole point of `manualLocationAtom`: this is a one-off act of choosing,
     * the same as picking a location from the header, and from now on that
     * store is the source of truth. Native draws the line in the same place -
     * `CityScreen` calls `setManualLocation`, `useOnboardingFlow` does not.
     */
    setManualLocation({
      latitude: resolved.latitude.toString(),
      longitude: resolved.longitude.toString(),
      label,
      // A named city, so home's city row shows it on one line with nothing
      // under it.
      source: "city",
    });

    completeWithCity({
      ...city,
      latitude: resolved.latitude,
      longitude: resolved.longitude,
      placeId: resolved.placeId,
      label,
    });
  };

  const selectBrowseCity = (city: OnboardingCityEntry) => {
    // The browse grid is built from `facets.locations`, which only lists cities
    // that already have mapped venues — everything in it is covered.
    const chosen: OnboardingCity = {
      name: city.name,
      country: city.country,
      covered: true,
      venueCount: city.count,
    };
    choose(chosen, () => resolveCity(chosen));
  };

  /**
   * Resolving a suggestion is also what closes the Google autocomplete session,
   * so it happens on every click, covered or not — skipping it would bill the
   * typing session per keystroke.
   */
  const selectSuggestion = (item: UnifiedSuggestItem) => {
    const chosen: OnboardingCity = {
      name: item.displayText,
      country: item.data?.country ?? item.subtitle,
      // Same test the row's pin colour uses: only an explicit `false` means
      // uncovered, so a suggestion that omits the flag is treated as covered.
      covered: item.data?.covered !== false,
      venueCount: item.data?.venueCount ?? undefined,
    };
    choose(chosen, () => selectCity(item));
  };

  const suggestionSubtitle = (item: UnifiedSuggestItem) => {
    const country = item.data?.country ?? item.subtitle;
    const count = item.data?.venueCount;
    return count != null
      ? `${country} • ${formatPlaces(i18n, count)}`
      : `${country} • ${i18n.get("onboardingCityAddInformation")}`;
  };

  const hasResults = coveredGroups.length > 0 || needsHelp.length > 0;

  /**
   * Whatever is on screen answers a different term than the one typed — either
   * the previous keystroke's results, or nothing at all before the first
   * search. Derived at render rather than read off a loading flag, because the
   * flags are set by the hook's effects and are still false on the render that
   * first shows `isSearching`.
   */
  const isStale = isSearching && searchedTerm !== trimmedTerm;
  const isLoadingResults = isStale || isLoadingCovered || isLoadingNeedsHelp;

  /**
   * "Nothing matched", as opposed to "we have not looked yet". `isStale` is
   * what separates the two: without it the empty state flashes on the first
   * keystroke, in the frame before the search has started.
   */
  const hasNoResults = isSearching && !isLoadingResults && !hasResults;

  /** Nothing current to show yet — the first search, or a term with no answer. */
  const showSkeleton = isSearching && isLoadingResults && !hasResults;

  const sectionTitle = (text: string) => (
    <h3 className="body-m-medium text-fg-neutral-strong">{text}</h3>
  );

  const renderSuggestionGroup = (
    groupTitle: string,
    items: UnifiedSuggestItem[],
  ) => (
    <div key={groupTitle} className="flex flex-col gap-4">
      {sectionTitle(groupTitle)}

      <div className="divide-y divide-border-token-brand-minimal overflow-hidden rounded-lg border border-border-token-brand-minimal">
        {items.map((item) => {
          const key = `${item.data?.country ?? item.subtitle}|${item.displayText}`;
          return (
            <OnboardingCityRow
              key={key}
              name={item.displayText}
              subtitle={suggestionSubtitle(item)}
              pin={item.data?.covered === false ? "grey" : "orange"}
              isSelecting={selectingKey === key}
              onSelect={() => selectSuggestion(item)}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={ariaLabel ?? title}
      stickyTop={
        <div className="flex flex-col gap-4">
          {header ?? (
            <OnboardingStepHeader
              stepNumber={stepNumber}
              totalSteps={totalSteps}
              title={title}
              subtitle={i18n.get("onboardingCitySubtitle")}
            />
          )}

          <div className="flex flex-col gap-2">
            <div className="relative">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label={backLabel}
                  className="focus-blue absolute left-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg hover:bg-bg-brand-ghost-hover"
                >
                  <ArrowLeftIcon
                    size={20}
                    aria-hidden="true"
                    className="text-fg-neutral-strong"
                  />
                </button>
              ) : (
                <MagnifyingGlassIcon
                  size={20}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-neutral-subtle"
                />
              )}
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={
                  onBack
                    ? (event) => {
                        if (event.key === "Escape") {
                          event.preventDefault();
                          onBack();
                        }
                      }
                    : undefined
                }
                placeholder={i18n.get("onboardingCitySearchPlaceholder")}
                aria-label={i18n.get("onboardingCitySearchPlaceholder")}
                autoComplete="off"
                className="focus-blue body-base-medium h-11 w-full rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default pl-10 pr-3 text-fg-neutral-strong placeholder:text-fg-neutral-subtle"
              />
            </div>

            {/* Figma drops the hint once results are on screen. */}
            {isSearching ? null : (
              <p className="body-m-medium flex items-start gap-1.5 text-fg-neutral-subtle">
                <InfoIcon
                  size={16}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0"
                />
                {i18n.get("onboardingCitySearchHint")}
              </p>
            )}
          </div>
        </div>
      }
    >
      {/*
        One region for both states, so a screen reader hears the result count
        change as the user types rather than nothing at all. `polite` because
        the field they are typing in must keep focus.
      */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {isSearching && !isLoadingResults
          ? hasNoResults
            ? i18n.get("onboardingCityNoMatch").replace("{term}", trimmedTerm)
            : i18n.get("onboardingCityResultsAvailable")
          : ""}
      </div>

      {isSearching ? (
        <div className="flex flex-col gap-6">
          {showSkeleton ? <SuggestionSkeleton /> : null}

          {coveredGroups.map((group) =>
            renderSuggestionGroup(
              `${group.country} (${group.cities.length})`,
              group.cities,
            ),
          )}

          {needsHelp.length > 0
            ? renderSuggestionGroup(
                i18n.get("onboardingCityNeedsHelp"),
                needsHelp,
              )
            : null}

          {/* One spinner for both tiers rather than one each: they finish
              ~350ms apart, and two stacked circles read as two separate things
              loading when it is one list still filling in. */}
          {!showSkeleton && isLoadingResults ? (
            <div className="flex justify-center">
              <CircleNotchIcon
                size={24}
                aria-hidden="true"
                className="animate-spin text-fg-brand-strong"
              />
            </div>
          ) : null}

          {hasNoResults ? (
            <p className="body-m-medium text-center text-fg-neutral-subtle">
              {i18n.get("onboardingCityNoMatch").replace("{term}", trimmedTerm)}
            </p>
          ) : null}
        </div>
      ) : (
        <OnboardingCityBrowse
          onSelect={selectBrowseCity}
          selectingKey={selectingKey}
        />
      )}
    </OnboardingModal>
  );
};

export default CityStep;
